import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  map,
  of,
  tap,
  throwError,
  switchMap,
  from,
} from 'rxjs';
import { ApiResponse } from 'app/shared/models/api.interface';
import { Router } from '@angular/router';
import { FirebaseAppService } from 'app/core/firebase/firebase-app.service';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { AuditService } from 'app/core/services/audit.service';
import { environment } from 'environments/environment';

declare const ngDevMode: boolean | undefined;

export interface AuthUser {
  id: string;
  user: string;
  name: string;
  role: string;
  token: string;
  refreshToken: string;
  expires_in: number; // seconds
  tokenFirebase?: string; // Firebase custom token
}

export interface RefreshResponseData {
  access_token: string;
  refresh_token: string;
  expires_in: number; // seconds
}

interface LoginPayload {
  user: string;
  password: string;
  type: string;
}

interface RefreshPayload {
  refreshToken: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private firebaseAppService = inject(FirebaseAppService);
  private auditService = inject(AuditService);

  private readonly AUTH_BASE_URL = environment.apiUrls.authService;

  private readonly STORAGE_KEYS = {
    accessToken: 'auth.access_token',
    refreshToken: 'auth.refresh_token',
    expiresAt: 'auth.expires_at', // epoch ms
    user: 'auth.user',
  } as const;

  private currentUserSubject = new BehaviorSubject<AuthUser | null>(
    this.getStoredUser()
  );
  currentUser$ = this.currentUserSubject.asObservable();

  private get headers() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'message-id':
        (crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2),
      location: (globalThis as any)?.location?.hostname || '192.168.1.100',
    });
  }

  get token(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.accessToken);
  }

  get refresh(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.refreshToken);
  }

  get isAuthenticated(): boolean {
    const token = this.token;
    const exp = Number(
      localStorage.getItem(this.STORAGE_KEYS.expiresAt) || '0'
    );
    return !!token && Date.now() < exp;
  }

  get currentUser(): AuthUser | null {
    return this.currentUserSubject.value;
  }

  get userId(): string | null {
    return this.currentUserSubject.value?.id || null;
  }

  login(user: string, password: string): Observable<AuthUser> {
    const payload: LoginPayload = { user, password, type: 'internos' };
    return this.http
      .post<ApiResponse<AuthUser>>(
        `${this.AUTH_BASE_URL}/v1/auth/login`,
        payload,
        {
          headers: this.headers,
        }
      )
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.message || 'Error de autenticación');
          }
          const data = res.data;
          this.storeSession(data);
          return data;
        })
      );
  }

  ensureFirebaseAuthOnStartup(): void {
    this.firebaseAppService.getFirebaseApp();
  }

  refreshToken(): Observable<RefreshResponseData | null> {
    const refreshToken = this.refresh;
    if (!refreshToken) return of(null);

    const payload: RefreshPayload = { refreshToken };
    return this.http
      .post<ApiResponse<RefreshResponseData>>(
        `${this.AUTH_BASE_URL}/v1/auth/refresh`,
        payload,
        { headers: this.headers }
      )
      .pipe(
        map((res) => {
          if (!res?.success) throw new Error(res?.message || 'Refresh failed');
          const data = res.data;
          this.updateTokens(data);
          return data;
        }),
        catchError((err) => {
          // On refresh failure, clear session and redirect to login.
          this.clearSession();
          return throwError(() => err);
        })
      );
  }

  logout(): Observable<ApiResponse<null> | null> {
    const user = this.currentUserSubject.value;
    const token = this.token;
    if (!user || !token) {
      this.clearSession();
      return of(null);
    }

    // Auditoría de logout
    this.auditService.sendAuditLog({
      usuario: user.user,
      accion: 'LOGOUT',
      fechaHora: new Date().toISOString(),
      seccion: 'AUTHENTICATION',
      sistema: 'AUCTION_SYSTEM'
    }).subscribe({
      error: (err) => console.warn('Error en auditoría de logout:', err)
    });

    const body = { user: user.id };
    const headers = this.headers.set('Authorization', `Bearer ${token}`);

    return this.http
      .post<ApiResponse<null>>(`${this.AUTH_BASE_URL}/v1/auth/logout`, body, {
        headers,
      })
      .pipe(
        catchError((err) => of(err?.error as ApiResponse<null>)),
        tap(() => this.clearSession())
      );
  }

  loginWithFirebase(user: string, password: string): Observable<AuthUser> {
    return this.login(user, password).pipe(
      switchMap((data) =>
        from(this.signInToFirebase(data?.tokenFirebase)).pipe(
          map(() => data),
          tap((userData) => {
            // Auditoría de login exitoso
            this.auditService.sendAuditLog({
              usuario: userData.user || user,
              accion: 'LOGIN',
              fechaHora: new Date().toISOString(),
              seccion: 'AUTHENTICATION',
              sistema: 'AUCTION_SYSTEM'
            }).subscribe({
              error: (err) => console.warn('Error en auditoría de login:', err)
            });
          })
        )
      )
    );
  }

  private storeSession(user: AuthUser) {
    const expiresAt = Date.now() + (user.expires_in ?? 900) * 1000;

    localStorage.setItem(this.STORAGE_KEYS.accessToken, user.token);
    localStorage.setItem(this.STORAGE_KEYS.refreshToken, user.refreshToken);
    localStorage.setItem(this.STORAGE_KEYS.expiresAt, String(expiresAt));
    localStorage.setItem(this.STORAGE_KEYS.user, JSON.stringify(user));

    this.currentUserSubject.next(user);
  }

  private updateTokens(tokens: RefreshResponseData) {
    const user = this.currentUserSubject.value;
    const expiresAt = Date.now() + (tokens.expires_in ?? 900) * 1000;

    localStorage.setItem(this.STORAGE_KEYS.accessToken, tokens.access_token);
    localStorage.setItem(this.STORAGE_KEYS.refreshToken, tokens.refresh_token);
    localStorage.setItem(this.STORAGE_KEYS.expiresAt, String(expiresAt));

    if (user) {
      const updated: AuthUser = {
        ...user,
        token: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expires_in: tokens.expires_in,
      };
      localStorage.setItem(this.STORAGE_KEYS.user, JSON.stringify(updated));
      this.currentUserSubject.next(updated);
    }
  }

  private clearSession() {
    localStorage.removeItem(this.STORAGE_KEYS.accessToken);
    localStorage.removeItem(this.STORAGE_KEYS.refreshToken);
    localStorage.removeItem(this.STORAGE_KEYS.expiresAt);
    localStorage.removeItem(this.STORAGE_KEYS.user);
    this.currentUserSubject.next(null);
    this.firebaseSignOut().catch(() => {});
    // Navigate to login
    try {
      this.router.navigate(['/login']);
    } catch {
      // ignore router errors during early bootstrap
    }
  }

  private getStoredUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEYS.user);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  private async signInToFirebase(customToken?: string): Promise<void> {
    if (!customToken) return;
    const app = await this.firebaseAppService.getFirebaseApp();
    if (!app) return;
    const auth = getAuth(app);
    try {
      await signInWithCustomToken(auth, customToken);
    } catch (error) {
      if (typeof ngDevMode !== 'undefined' && ngDevMode) {
        console.warn(
          '[AuthService] No se pudo iniciar sesión en Firebase.',
          error
        );
      }
    }
  }

  private async firebaseSignOut(): Promise<void> {
    const app = await this.firebaseAppService.getFirebaseApp();
    if (!app) return;
    const auth = getAuth(app);
    try {
      await signOut(auth);
    } catch {
      // ignore sign-out errors
    }
  }
}
